import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { 
  CheckCircle, 
  Clock, 
  Shield, 
  Wifi, 
  Star, 
  X,
  Heart,
  Wind,
  Zap,
  Settings,
  ChevronRight,
  ChevronLeft
} from 'lucide-react';
import { Button } from './ui/button';

interface ProductInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  product: any;
  type: 'equipo' | 'mascarilla' | 'plan' | 'retention';
  onKeepPlan?: () => void;
  onRemovePlan?: () => void;
}

const ProductInfoModal: React.FC<ProductInfoModalProps> = ({ 
  isOpen, 
  onClose, 
  product, 
  type,
  onKeepPlan,
  onRemovePlan 
}) => {
  if (!product) return null;

  const getDetailedInfo = () => {
    if (type === 'plan' || type === 'retention') {
      const baseDescription = 'El programa buno+ es un plan integral de acompañamiento diseñado para maximizar los beneficios de tu tratamiento CPAP y asegurar una adherencia exitosa.';
      const retentionDescription = `¿Continuar sin buno+?`;
      
      return {
        description: type === 'retention' ? retentionDescription : baseDescription,
        specifications: {
          'Duración': 'Durante todo el tratamiento',
          'Ajustes remotos': 'Basados en tus datos de sueño',
          'Especialistas': 'Equipo médico certificado',
        },
        benefits: type === 'retention' ? [
          'Acompañamiento personalizado con especialistas',
          'Ajustes basados en tus datos de uso',
          'Reportes médicos para seguimiento',
          'Mayor probabilidad de éxito en tu tratamiento'
        ] : [
          '50% mayor adherencia al tratamiento comparado con terapia sin acompañamiento',
          'Seguimiento para optimizar tu adaptación',
          'Soporte técnico cuando lo necesites',
          'Resolución de problemas y dudas vía WhatsApp',
        ],
        warranty: 'Acompañamiento garantizado durante todo tu tratamiento',
        includes: [
          'Acceso a portal educativo para onboarding personalizado',
          'Soporte por WhatsApp en horario de atención',
          'Ajustes remotos de configuración',
          'Soporte técnico'
        ]
      };
    } else if (type === 'equipo') {
      if (product.marca === 'ResMed') {
        return {
          description: 'El AirSense 10 AutoSet es un dispositivo CPAP automático de última generación que ajusta automáticamente la presión según tus necesidades durante toda la noche.',
          specifications: {
            'Rango de presión': '4-20 cmH2O',
            'Nivel de ruido': '≤26.6 dBA',
            'Dimensiones': '25.5 × 15 × 11.9 cm',
            'Peso': '1.37 kg',
            'Humidificador': 'HumidAir integrado',
            'Pantalla': 'LCD a color',
            'Conectividad': 'Wireless y celular',
            'Tarjeta SD': 'Incluida'
          },
          benefits: [
            'Algoritmo AutoSet que detecta y responde a obstrucciones',
            'Función EPR (alivio de presión espiratoria) para mayor comodidad',
            'Rampa inteligente que se ajusta automáticamente',
            'Tecnología Easy-Breathe para un motor silencioso',
            'Detección automática de máscara',
            'Reportes detallados en myAir app'
          ],
          warranty: '1 año de garantía completa',
          includes: [
            'Dispositivo AirSense 10 AutoSet',
            'HumidAir humidificador integrado',
            'Cámara de agua',
            'Tubing estándar',
            'Tarjeta SD',
            'Fuente de poder',
            'Manual de usuario'
          ]
        };
      } else {
        return {
          description: 'El Yuwell YH-680B es un dispositivo CPAP automático confiable que ofrece terapia efectiva con excelente relación calidad-precio.',
          specifications: {
            'Rango de presión': '4-20 cmH2O',
            'Nivel de ruido': '≤30 dBA',
            'Dimensiones': '24 × 14 × 12 cm',
            'Peso': '1.5 kg',
            'Humidificador': 'Integrado',
            'Pantalla': 'LCD monocromática',
            'Conectividad': 'Bluetooth',
            'Memoria': 'Interna'
          },
          benefits: [
            'Algoritmo de detección automática de apneas',
            'Función EPR básica para comodidad',
            'Rampa ajustable manualmente',
            'Motor silencioso optimizado',
            'Fácil operación con controles intuitivos',
            'Excelente relación calidad-precio'
          ],
          warranty: '1 año de garantía',
          includes: [
            'Dispositivo YH-680B',
            'Tubing estándar',
            'Filtro de aire',
            'Fuente de poder',
            'Manual de usuario',
            'Estuche de viaje'
          ]
        };
      }
    } else {
      // Mascarilla info
      if (product.modelo.includes('AirFit N20')) {
        return {
          description: 'La AirFit N20 es una mascarilla nasal premium que combina un sellado excepcional con máxima comodidad y campo visual despejado.',
          specifications: {
            'Tipo': 'Mascarilla nasal',
            'Tallas disponibles': 'Pequeña, Mediana, Grande',
            'Material': 'Silicona ultrasuave',
            'Peso': '78g (talla M)',
            'Campo visual': '20% más amplio',
            'Conexión': 'Universal 22mm',
            'Limpieza': 'Diaria recomendada'
          },
          benefits: [
            'Tecnología InfinitySeal para sellado adaptable',
            'Marco minimalista que no obstruye la visión',
            'Almohadilla nasal UltraSoft para máxima comodidad',
            'Conexión rápida QuickFit para fácil ajuste',
            'Tubing que gira 360° para mayor libertad de movimiento',
            'Compatible con la mayoría de equipos CPAP'
          ],
          warranty: '6 meses de garantía',
          includes: [
            'Marco de mascarilla',
            'Almohadilla nasal UltraSoft',
            'Arnés de cabeza',
            'Tubing giratorio',
            'Guía de ajuste',
            'Manual de limpieza'
          ]
        };
      } else if (product.modelo.includes('AirFit F20')) {
        return {
          description: 'La AirFit F20 es una mascarilla oronasal de cobertura completa diseñada para quienes respiran por boca y nariz durante el sueño.',
          specifications: {
            'Tipo': 'Mascarilla oronasal',
            'Tallas disponibles': 'Pequeña, Mediana, Grande',
            'Material': 'Silicona ultrasuave',
            'Peso': '145g (talla M)',
            'Cobertura': 'Boca y nariz',
            'Conexión': 'Universal 22mm',
            'Limpieza': 'Diaria recomendada'
          },
          benefits: [
            'Tecnología InfinitySeal para sellado óptimo',
            'Almohadilla facial UltraSoft de cobertura completa',
            'Marco flexible que se adapta a diferentes rostros',
            'Válvula de escape QuietAir para reducir ruido',
            'Tubing que gira 360° para mayor comodidad',
            'Ideal para respiradores bucales'
          ],
          warranty: '6 meses de garantía',
          includes: [
            'Marco de mascarilla',
            'Almohadilla facial UltraSoft',
            'Arnés de cabeza',
            'Tubing giratorio',
            'Guía de ajuste',
            'Manual de limpieza'
          ]
        };
      } else if (product.modelo.includes('YN-03')) {
        return {
          description: 'La Yuwell YN-03 es una mascarilla nasal que ofrece comodidad básica y funcionalidad confiable para terapia CPAP con excelente relación calidad-precio.',
          specifications: {
            'Tipo': 'Mascarilla nasal',
            'Tallas disponibles': 'Pequeña, Mediana, Grande',
            'Material': 'Silicona estándar',
            'Peso': '85g (talla M)',
            'Conexión': 'Universal 22mm',
            'Limpieza': 'Diaria recomendada'
          },
          benefits: [
            'Liviana y práctica',
            'Almohadilla nasal cómoda',
            'Fácil limpieza y mantenimiento',
            'Excelente relación calidad-precio',
            'Compatible con la mayoría de equipos',
            'Ideal para usuarios nuevos en CPAP'
          ],
          warranty: '3 meses de garantía',
          includes: [
            'Marco de mascarilla',
            'Almohadilla nasal',
            'Arnés de cabeza',
            'Tubing estándar',
            'Manual de usuario'
          ]
        };
      } else {
        return {
          description: 'La Yuwell YF-02 es una mascarilla oronasal accesible que proporciona cobertura completa para respiradores bucales con buen sellado.',
          specifications: {
            'Tipo': 'Mascarilla oronasal',
            'Tallas disponibles': 'Pequeña, Mediana, Grande',
            'Material': 'Silicona estándar',
            'Peso': '150g (talla M)',
            'Cobertura': 'Boca y nariz',
            'Conexión': 'Universal 22mm',
            'Limpieza': 'Diaria recomendada'
          },
          benefits: [
            'Cobertura completa boca y nariz',
            'Buen sellado para terapia efectiva',
            'Precio accesible sin comprometer calidad',
            'Diseño versátil para diferentes rostros',
            'Fácil mantenimiento',
            'Compatible con equipos estándar'
          ],
          warranty: '3 meses de garantía',
          includes: [
            'Marco de mascarilla',
            'Almohadilla facial',
            'Arnés de cabeza',
            'Tubing estándar',
            'Manual de usuario'
          ]
        };
      }
    }
  };

  const info = getDetailedInfo();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-full max-h-full h-screen w-screen p-0 gap-0 sm:max-w-4xl sm:max-h-[90vh] sm:h-auto sm:w-[90vw] flex flex-col"
        hideCloseButton={type === 'retention'}
        aria-describedby='product-info-description'
      >
        {/* Modal content wrapper - uses flex to ensure proper layout */}
        <div className="flex flex-col h-full min-h-0">
          {/* Header section - fixed at top */}
          {type === 'retention' ? <>
          <DialogTitle></DialogTitle></> : <>
            <DialogHeader className="px-4 pt-6 pb-3 z-10 border-border border-b flex-shrink-0">
              <div className="flex items-start gap-4">
                {type === 'plan' ? <>
                  <div className="w-16 h-16 flex-shrink-0 bg-white rounded-lg overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </> : <>
                  <div className="w-24 h-24 flex-shrink-0 bg-white rounded-lg overflow-hidden">
                    <img 
                      src={product.imagen} 
                      alt={`${product.marca} ${product.modelo}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </>}

                <div className="flex-1">
                  <div className="mb-2">
                    <DialogTitle className="text-2xl sm:text-3xl text-left text-primary font-bold mb-1">
                      {type === 'plan' ? product.name : product.modelo}
                    </DialogTitle>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Badge variant="outline" className="text-sm">
                        {type === 'plan' ? product.brand : product.marca}
                      </Badge>
                      {type === 'mascarilla' && (
                        <Badge variant="outline" className="text-sm">
                          {product.tipo}
                        </Badge>
                      )}
                      {(type === 'plan') && product.recommended && (
                        <Badge variant="default" className="text-sm bg-accent text-accent-foreground">
                          Recomendado
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </DialogHeader>
          </>}
          
          {/* Scrollable content area - takes remaining space */}
          <div className="flex-1 space-y-6 overflow-y-auto pb-6 px-4 min-h-0">
          {/* Description */}
          {type === 'retention' ? <>
          <div className='flex flex-row items-center gap-2 mb-6 pt-4'>
            <Button
                variant="ghost"
                size="icon"
                onClick={() => onClose()}
                className="h-10 w-10 rounded-full block top-4 left-4 z-50"
                aria-label="Go back"
              >
              <ChevronLeft className="h-5 w-10" />
            </Button> 
            <h1 className='text-xl font-bold text-center text-primary'>
              {info.description}</h1>
          </div>
            
          </> : <><Card className='mt-4'>
            <CardContent className="p-6">
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{info.description}</p>
            </CardContent>
          </Card></>}
          

          {/* Retention-specific highlights */}
          {type === 'retention' && (
            <>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-6">
                  <p className="text-center">
                    <strong className="text-xl text-amber-800">Las personas que usan CPAP con un programa como el nuestro tienen una adherencia 50% mayor</strong>
                  </p>
                  <p className="text-amber-700 text-center mt-2">
                    y mejores resultados en su tratamiento.
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <p className="text-blue-800 text-center font-medium">
                    Te recomendamos mantener el programa para obtener los mejores resultados.
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            { type !== 'retention' && (
              <>{/* Specifications */}
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
                    <Settings className="h-5 w-5 text-primary" />
                    Especificaciones técnicas
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(info.specifications).map(([key, value]) => (
                      <div key={key} className="bg-muted/30 rounded-lg p-3 border border-border/50">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                          {key}
                        </div>
                        <div className="text-sm font-semibold text-foreground">
                          {value}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card></>
            )}

            {/* Benefits */}
            <Card>
              <CardContent className="p-4 sm:p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
                  <Star className="h-5 w-5 text-primary" />
                  Beneficios clave
                </h3>
                <ul className="space-y-3">
                  {info.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                      <span className="text-md text-muted-foreground">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Warranty & Includes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {type === 'equipo' && (
              <Card>
                <CardContent className="p-4 sm:p-6">
                  <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
                    <Shield className="h-5 w-5 text-primary" />
                    Garantía
                  </h3>
                  <p className="text-muted-foreground">{info.warranty}</p>
                </CardContent>
              </Card>
            )}

            <Card className={type === 'equipo' ? '' : 'md:col-span-2'}>
              <CardContent className="p-4 sm:p-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold mb-4">
                  <Heart className="h-5 w-5 text-primary" />
                  Incluye
                </h3>
                <ul className="space-y-2">
                  {info.includes.map((item, index) => (
                    <li key={index} className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-success flex-shrink-0" />
                      <span className="text-md text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Additional product info */}
          {type === 'equipo' && (
            <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
              <CardContent className="p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Wifi className="h-6 w-6 text-primary" />
                    <span className="font-medium">Conectividad</span>
                    <span className="text-md text-muted-foreground">{product.conectividad}</span>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <Shield className="h-6 w-6 text-primary" />
                    <span className="font-medium">Garantía</span>
                    <span className="text-md text-muted-foreground">{product.garantia}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {type === 'mascarilla' && (
            <Card className="bg-gradient-to-r from-primary/5 to-accent/5 border-primary/20">
              <CardContent className="p-4 sm:p-6">
                <div className="text-center">
                  <h4 className="font-semibold mb-2">Compatibilidad</h4>
                  <div className="flex flex-wrap justify-center gap-2">
                    {product.compatibilidad?.map((equipo: string, index: number) => (
                      <Badge key={index} variant="outline" className="text-md">
                        {equipo}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          </div>
          
          {/* Footer section for retention buttons - fixed at bottom of modal */}
          {type === 'retention' && onKeepPlan && onRemovePlan && (
            <div className="flex-shrink-0 bg-white border-t border-border p-4 shadow-lg" style={{ borderRadius: '0 0 20px 20px' }}>
              <div className="mx-auto max-w-4xl grid grid-cols-1 gap-3">
                <Button className="min-h-touch px-6 sm:px-8 gap-0 w-full text-lg" onClick={onKeepPlan}>
                  <span className="">Continuar con buno+</span>
                  <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>
                  <div className="h-0 w-0 absolute" style={{ top: '0' , right: '8rem' }}>
                    <Badge variant="default" className="bg-accent text-accent-foreground hover:bg-accent/80">
                      Recomendado
                    </Badge>
                  </div>
                </Button>
                <a onClick={onRemovePlan} className="underline text-lg text-center text-muted-foreground cursor-pointer">
                  Continuar sin buno+
                </a>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductInfoModal;